import React, {useCallback, useContext} from 'react';


import {ConfirmationModal} from 'src/components/confirmation-modal';
import {FormInput, NumberInput, PasswordInput} from 'src/components/input';
import {RequiredMark} from 'src/components/required-mark';
import {ResponsiveElementGroup} from 'src/components/responsive-element-group';
import {Skeleton} from 'src/components/skeleton';
import {CustomValidatedControl, ValidationContext} from 'src/contexts/validation-context';
import {CredentialsType} from 'src/openapi/admin/models/CredentialsType';
import {CrmType as CrmTypes} from 'src/openapi/admin/models/CrmType';

import Button from '@pb/kit-component-button';
import {CheckBox} from '@pb/kit-component-checkbox';
import FormSection from '@pb/kit-component-form-section';
import {Integer} from '@pb/kit-component-formatters';
import {Group, Item} from '@pb/kit-component-group';
import Label from '@pb/kit-component-label';
import {RadioButton, RadioButtons} from '@pb/kit-component-radio-button';
import {LocaleContext} from '@pb/kit-context-locale';

import {EntitiesSection} from '../../components/entities-section';

import {
    CLASSIC_DOMAIN_VALIDATION_CONTROL,
    INVALID_CLASSIC_DOMAIN,
    INVALID_LIGHTNING_DOMAIN,
    LIGHTNING_DOMAIN_VALIDATION_CONTROL,
    SALESFORCE_VALIDATION_ERRORS,
} from './constants';
import {SalesforceSectionSkeleton} from './salesforce-section-skeleton';
import {isSalesforceTestReady} from './utils/isSalesforceTestReady';

import type {Dispatch, SetStateAction} from 'react';
import type {CrmAccountSettings} from 'src/openapi/admin/models/CrmAccountSettings';
import type {Error} from 'src/openapi/admin/models/Error';
import type {GetCrmLimit} from 'src/openapi/admin/models/GetCrmLimit';
import type {LinkageStatistics} from 'src/openapi/admin/models/LinkageStatistics';
import type {SalesforceAccountSettings} from 'src/openapi/admin/models/SalesforceAccountSettings';
import type {SalesforceCredentials} from 'src/openapi/admin/models/SalesforceCredentials';
import type {CheckBoxChangeData} from 'src/typedef';

type Props = {
    isLoading: boolean,
    isSyncLinkageLoading: boolean,
    isOAuthRedirectUrlLoading: boolean,
    isLinkageStatisticLoading: boolean,
    isConnectionStatusLoading: boolean,
    linkageStatisticLoadingError: Error | null,
    disabled?: boolean,
    readonly?: boolean,
    orgId?: string,

    limits: GetCrmLimit,
    linkageStatistic: LinkageStatistics | null,
    credentials: SalesforceCredentials,
    settings: SalesforceAccountSettings,

    onChangeLimits: (limits: GetCrmLimit) => void,
    onSyncLinkageStatistic: () => void,
    onChangeCredentials: Dispatch<SetStateAction<SalesforceCredentials>>,
    onChangeSettings: Dispatch<SetStateAction<SalesforceAccountSettings>>,
    onSetupPasswordLogin: () => void,
    onSetupOAuthLogin: () => void,
    onRevokeOAuthToken: () => void,
};

const validationRegex = /(https?:\/\/)?[\w\-~]+(\.[\w\-~]+)+(\/[\w\-~]*)*(#[\w]*)?(\?.*)?/;

export const SalesforceSection = ({
    isLoading,
    isSyncLinkageLoading,
    isOAuthRedirectUrlLoading,
    isLinkageStatisticLoading,
    isConnectionStatusLoading,
    linkageStatisticLoadingError,

    limits,
    credentials,
    linkageStatistic,
    settings,
    orgId,

    onChangeLimits,
    onChangeCredentials,
    onChangeSettings,
    onSyncLinkageStatistic,
    onSetupOAuthLogin,
    onSetupPasswordLogin,
    onRevokeOAuthToken,
    disabled,
    readonly,
}: Props) => {
    const {addError, removeError} = useContext(ValidationContext);
    const {locale} = useContext(LocaleContext);

    const changeMaxUsers = useCallback((maxUsers?: number) => {
        onChangeLimits({...limits, maxUsers: maxUsers || 0});
    }, [limits, onChangeLimits]);

    const changeMaxImports = useCallback((maxImports?: number) => {
        onChangeLimits({...limits, maxImports: maxImports || 0});
    }, [limits, onChangeLimits]);

    const changeImportCredits = useCallback((importCredits?: number) => {
        onChangeLimits({...limits, importCredits: importCredits || 0});
    }, [limits, onChangeLimits]);

    const changeCredentialsType = useCallback((credentialsType: CredentialsType) => {
        onChangeCredentials(prevState => ({...prevState, credentialsType}));
    }, [onChangeCredentials]);

    const changeUseSandbox = useCallback((changeData: CheckBoxChangeData) => {
        onChangeCredentials(prevState => ({
            ...prevState,
            sandbox: changeData.event.currentTarget.checked,
        }));
    }, [onChangeCredentials]);

    const changeLogin = useCallback((login: string) => {
        onChangeCredentials(prevState => ({
            ...prevState,
            passwordCredentials: {...prevState.passwordCredentials, login},
        }));
    }, [onChangeCredentials]);

    const changePassword = useCallback((password: string) => {
        onChangeCredentials(prevState => {
            const passwordCredentials = password ? {
                ...prevState.passwordCredentials,
                password,
                passwordChanged: true,
                passwordNotEmpty: true,
            } : prevState.passwordCredentials;

            return {...prevState, passwordCredentials};
        });
    }, [onChangeCredentials]);

    const clearPassword = useCallback(() => {
        onChangeCredentials(prevState => ({
            ...prevState,
            passwordCredentials: {
                ...prevState.passwordCredentials,
                passwordChanged: true,
                passwordNotEmpty: false,
                password: '',
            },
        }));
    }, [onChangeCredentials]);

    const changeSecurityToken = useCallback((securityToken: string) => {
        onChangeCredentials(prevState => ({...prevState, securityToken}));
    }, [onChangeCredentials]);

    const changeAuthEndpoint = useCallback((authEndpoint: string) => {
        onChangeCredentials(prevState => ({...prevState, authEndpoint}));
    }, [onChangeCredentials]);

    const changeClassicDomain = useCallback((classicDomain: string) => {
        onChangeSettings(prevState => ({...prevState, classicDomain}));

        if (classicDomain.match(validationRegex) && !classicDomain.includes(' ') || !classicDomain) {
            removeError(CLASSIC_DOMAIN_VALIDATION_CONTROL);
        } else {
            addError({
                code: INVALID_CLASSIC_DOMAIN,
                message: SALESFORCE_VALIDATION_ERRORS.INVALID_CLASSIC_DOMAIN,
                controlId: CLASSIC_DOMAIN_VALIDATION_CONTROL,
            });
        }
    }, [addError, removeError, onChangeSettings]);

    const changeLightningDomain = useCallback((lightningDomain: string) => {
        onChangeSettings(prevState => ({...prevState, lightningDomain}));

        if (lightningDomain.match(validationRegex) && !lightningDomain.includes(' ') || !lightningDomain) {
            removeError(LIGHTNING_DOMAIN_VALIDATION_CONTROL);
        } else {
            addError({
                code: INVALID_LIGHTNING_DOMAIN,
                message: SALESFORCE_VALIDATION_ERRORS.INVALID_LIGHTNING_DOMAIN,
                controlId: LIGHTNING_DOMAIN_VALIDATION_CONTROL,
            });
        }
    }, [addError, removeError, onChangeSettings]);

    return (
        <Skeleton shown={isLoading} view={<SalesforceSectionSkeleton/>}>
            <Group direction="v" gap="l">
                <FormSection headline="Credentials">
                    <Group direction="v">
                        <Item>
                            <Label><RequiredMark label="Authentication type"/></Label>
                            <Group gap="l">
                                <RadioButtons
                                    disabled={disabled}
                                    name="AuthType"
                                    value={credentials.credentialsType}
                                    onChange={changeCredentialsType}>
                                    <Group gap="l" alignItems="center">
                                        <RadioButton
                                            showLabel={true}
                                            label="Password"
                                            value={CredentialsType.PASSWORD}
                                        />
                                        <RadioButton value={CredentialsType.OAUTH} label="OAuth"/>
                                    </Group>
                                </RadioButtons>
                                <CheckBox
                                    disabled={disabled}
                                    label="Use sandbox"
                                    checked={credentials.sandbox}
                                    onCheckBoxChange={changeUseSandbox}
                                />
                            </Group>
                        </Item>
                        {credentials.credentialsType === CredentialsType.PASSWORD ? (
                            <Group direction="v">
                                <ResponsiveElementGroup>
                                    <FormInput
                                        label="SF login"
                                        width={300}
                                        disabled={disabled}
                                        readOnly={readonly}
                                        placeholder="Salesforce login"
                                        value={credentials.passwordCredentials.login}
                                        onBlur={changeLogin}
                                    />
                                    <PasswordInput
                                        id="sf-password"
                                        label="SF password"
                                        width={300}
                                        disabled={disabled}
                                        readOnly={readonly}
                                        placeholder="Salesforce password"
                                        value={credentials.passwordCredentials.password}
                                        isNotEmpty={credentials.passwordCredentials.passwordNotEmpty}
                                        onBlur={changePassword}
                                        onClear={clearPassword}
                                    />
                                    <FormInput
                                        label="Security token"
                                        width={300}
                                        disabled={disabled}
                                        readOnly={readonly}
                                        placeholder="Security token"
                                        value={credentials.securityToken}
                                        onBlur={changeSecurityToken}
                                    />
                                    <FormInput
                                        label="Authorization endpoint"
                                        width={300}
                                        disabled={disabled}
                                        readOnly={readonly}
                                        placeholder="Authorization endpoint"
                                        value={credentials.authEndpoint || ''}
                                        onBlur={changeAuthEndpoint}
                                    />
                                </ResponsiveElementGroup>
                                <Item>
                                    <Button
                                        disabled={disabled || readonly || !isSalesforceTestReady(credentials)}
                                        busy={isConnectionStatusLoading}
                                        variant="Type.SECONDARY"
                                        onClick={onSetupPasswordLogin}>
                                        Save and Connect
                                    </Button>
                                </Item>
                            </Group>
                        ) : (
                            <ResponsiveElementGroup>
                                <Button
                                    busy={isOAuthRedirectUrlLoading}
                                    disabled={disabled || readonly}
                                    variant="Type.SECONDARY"
                                    onClick={onSetupOAuthLogin}>
                                    Setup OAuth login
                                </Button>
                                <ConfirmationModal
                                    title="Revoke OAuth token"
                                    message="
                                    This will remove the connection between the PitchbBook Plugin and Salesforce.
                                    Are you sure you want to revoke th OAuth token?
                                "
                                    onConfirm={onRevokeOAuthToken}>
                                    <Button
                                        disabled={disabled || readonly}
                                        variant="Type.SECONDARY">
                                        Revoke OAuth token
                                    </Button>
                                </ConfirmationModal>
                            </ResponsiveElementGroup>
                        )}
                    </Group>
                </FormSection>
                <FormSection headline="Endpoint Configuration">
                    <ResponsiveElementGroup>
                        <CustomValidatedControl name={CLASSIC_DOMAIN_VALIDATION_CONTROL}>
                            {error => (
                                <FormInput
                                    label="Salesforce classic domain"
                                    maxLength={64}
                                    width={300}
                                    disabled={disabled}
                                    readOnly={readonly}
                                    placeholder="Classic domain"
                                    value={settings.classicDomain}
                                    onBlur={changeClassicDomain}
                                    valid={error ? false : undefined}
                                    errorDescription={error}
                                />
                            )}
                        </CustomValidatedControl>
                        <CustomValidatedControl name={LIGHTNING_DOMAIN_VALIDATION_CONTROL}>
                            {error => (
                                <FormInput
                                    label="Salesforce Lightning domain"
                                    maxLength={64}
                                    width={300}
                                    disabled={disabled}
                                    readOnly={readonly}
                                    placeholder="Lightning domain"
                                    value={settings.lightningDomain}
                                    onBlur={changeLightningDomain}
                                    valid={error ? false : undefined}
                                    errorDescription={error}
                                />
                            )}
                        </CustomValidatedControl>
                    </ResponsiveElementGroup>
                </FormSection>
                <FormSection headline="CRM Limits">
                    <ResponsiveElementGroup>
                        <NumberInput
                            width={300}
                            label={<RequiredMark label="SFDC users"/>}
                            onBlur={changeMaxUsers}
                            readOnly={readonly}
                            placeholder="Max Salesforce Users"
                            disabled={disabled}
                            value={limits.maxUsers}
                            maxValue={2147483647}
                            displayedValue={Integer.format(limits.maxUsers, locale)}
                        />
                        <NumberInput
                            width={300}
                            label={<RequiredMark label="Imports & syncs"/>}
                            onBlur={changeMaxImports}
                            readOnly={readonly}
                            disabled={disabled}
                            placeholder="Max imports"
                            value={limits.maxImports || 0}
                            maxValue={2147483647}
                            displayedValue={Integer.format(limits.maxImports || 0, locale)}
                        />
                        <NumberInput
                            width={300}
                            label={<RequiredMark label="Import from PB"/>}
                            onBlur={changeImportCredits}
                            readOnly={readonly}
                            disabled={true}
                            placeholder="Import from PB"
                            value={limits.importCredits || 0}
                            maxValue={2147483647}
                            displayedValue={Integer.format(limits.importCredits || 0, locale)}
                        />
                    </ResponsiveElementGroup>
                </FormSection>
                <EntitiesSection
                    crmType={CrmTypes.SFCRM}
                    disabled={disabled || readonly}
                    isSyncLinkageLoading={isSyncLinkageLoading}
                    isLinkageStatisticLoading={isLinkageStatisticLoading}
                    linkageStatisticLoadingError={linkageStatisticLoadingError}
                    settings={settings}
                    orgId={orgId}
                    linkageStatistic={linkageStatistic}
                    onSyncLinkageStatistic={onSyncLinkageStatistic}
                    onChangeSettings={
                        onChangeSettings as Dispatch<
                            SetStateAction<SalesforceAccountSettings | CrmAccountSettings>
                        >
                    }
                />
            </Group>
        </Skeleton>
    );
};
